/* ------------------------------------------------------------------------- *
 * Idenza — Approval & deflection console (PaymentWorld merchant proof screen)
 *
 * A sales-facing "before and after" proof for the acquiring book: it replays
 * one merchant's month of card traffic through two lanes at once — the way the
 * merchant runs today, and with Idenza scoring pre-authorization — and lets the
 * approval rate and VAMP build out of the individual decisions. Idenza's costs
 * (abandoned 3DS, false positives, fraud that still slips) are shown on screen,
 * so it reads as observed rather than asserted.
 *
 * Self contained. One file, one default export, no local imports, no CSS
 * import, no Tailwind, no build step. Runtime dep is react-icons only, which is
 * already in the Navigator bundle, so it adds no new dependency.
 *
 *   import ApprovalUpliftConsole from "./ApprovalUpliftConsole";
 *   <ApprovalUpliftConsole />                       // standalone, own header
 *   <ApprovalUpliftConsole showHeader={false} />    // inside Navigator route
 *
 * To point it at live data, pass `data` as an array of transaction rows in the
 * shape returned by buildDataset() below, or pass `merchant`, `realm`,
 * `updatedAt` to relabel. onReplayEnd(summary) fires when the month finishes.
 *
 * ----- wire it into Navigator (same pattern as StablecoinRiskConsole) --------
 * 1) import at top of Navigator.js:
 *      import ApprovalUpliftConsole from "./ApprovalUpliftConsole";
 * 2) add a route (wrapped in BackToLaunch like the other dashboards):
 *      {showStablecoinDashboard && (
 *        <Route path="/approval" element={
 *          <>
 *            <BackToLaunch navigate={navigate} withRealm={withRealm} />
 *            <ApprovalUpliftConsole showHeader={false} realm={getCurrentRealm()} />
 *          </>
 *        } />
 *      )}
 * 3) surface it on the launch page by adding a third view to NavigatorLaunch
 *    DEMO_DATA.views (key: "approval", href: "/approval?realm=..."), and pass
 *    routes={{ acquiring, stablecoin, approval: withRealm("/approval") }} plus
 *    handle "approval" in onOpen. A NavItem to withRealm("/approval") works too.
 * ------------------------------------------------------------------------- */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  FiPlay, FiPause, FiSkipForward, FiChevronRight, FiActivity,
  FiTrendingUp, FiShield, FiAlertTriangle, FiZap, FiArrowRight,
} from "react-icons/fi";

/* =============================== tokens =============================== *
 * Matched to the Navigator shell and the other consoles: navy-to-blue
 * banner, near-white blue-grey page, teal/blue/green accents. */
const T = {
  navy: "#10214A", blue: "#2563EB", blueBright: "#2F6FED", blueSoft: "#E8F0FE",
  green: "#15803D", greenSoft: "#E7F4EC", greenFig: "#12805B",
  amber: "#C2680B", amberSoft: "#FDF2E3", red: "#DC2626", redSoft: "#FDECEC",
  teal: "#0E5F63", tealSoft: "#E3F0F0",
  page: "#F4F7FD", card: "#FFFFFF", line: "#E3E9F5", lineSoft: "#EFF3FA",
  text: "#0F1B33", sub: "#3E4C63", muted: "#64748B",
  sans: 'ui-sans-serif, system-ui, "Segoe UI", Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/* ========================= scoped stylesheet ========================== */
const STYLE_ID = "idz-approval-uplift-styles";
const css = `
.idzm-root { box-sizing: border-box; }
.idzm-root *, .idzm-root *::before, .idzm-root *::after { box-sizing: inherit; }
.idzm-shell { display: flex; min-height: 100%; }
.idzm-side { width: 260px; flex: 0 0 260px; padding: 18px 14px; }
.idzm-page { flex: 1; min-width: 0; }
.idzm-wrap { max-width: 1360px; margin: 0 auto; padding: 20px 24px 34px; }

.idzm-banner { border-radius: 16px; padding: 20px 24px; display: flex; align-items: center; gap: 16px;
  flex-wrap: wrap; background: linear-gradient(100deg, #10214A 0%, #1D3F8F 46%, #2F6FED 100%); }
.idzm-logo { width: 44px; height: 44px; border-radius: 10px; background: #0B1834; color: #fff; display: flex;
  align-items: center; justify-content: center; font-family: Georgia, "Times New Roman", serif; font-size: 23px; font-weight: 700; flex: 0 0 auto; }
.idzm-eyebrow { text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.13em; font-weight: 800; }
.idzm-fig { font-variant-numeric: tabular-nums; letter-spacing: -0.02em; font-weight: 800; font-family: var(--idzm-mono); }

.idzm-ctrl { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--idzm-line);
  border-radius: 13px; padding: 12px 16px; margin-top: 18px; flex-wrap: wrap; }
.idzm-play { border: 0; border-radius: 9px; padding: 10px 18px; font-size: 13.5px; font-weight: 700; cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px; background: var(--idzm-blue); color: #fff; min-width: 132px; justify-content: center; }
.idzm-sbtn { background: #fff; border: 1px solid var(--idzm-line); color: var(--idzm-muted); font-weight: 700; font-size: 12.5px;
  padding: 9px 12px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.idzm-sbtn.on { background: var(--idzm-navy); color: #fff; border-color: var(--idzm-navy); }
.idzm-prog { flex: 1; min-width: 150px; height: 8px; background: var(--idzm-line-soft); border-radius: 5px; overflow: hidden; }
.idzm-prog > div { height: 100%; background: var(--idzm-blue); width: 0; transition: width .12s linear; }
.idzm-progtxt { font-family: var(--idzm-mono); font-size: 12px; color: var(--idzm-muted); font-weight: 700; white-space: nowrap; }

.idzm-lanes { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
.idzm-lane { background: #fff; border: 1px solid var(--idzm-line); border-radius: 16px; padding: 18px 20px; position: relative; }
.idzm-lane.idz { border-color: #C6D6F5; box-shadow: 0 8px 24px rgba(37,99,235,0.06); }
.idzm-lane .lh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.idzm-lane .lt { font-size: 12.5px; font-weight: 800; letter-spacing: .06em; }
.idzm-tag { font-size: 10.5px; font-weight: 800; padding: 3px 9px; border-radius: 6px; letter-spacing: .04em; text-transform: uppercase; }
.idzm-duo { display: flex; gap: 26px; margin-bottom: 12px; }
.idzm-big .k { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--idzm-muted); margin-bottom: 5px; }
.idzm-big .v { font-size: 33px; line-height: 1; font-family: var(--idzm-mono); font-weight: 800; letter-spacing: -0.02em; }
.idzm-counts { display: flex; gap: 16px; font-size: 12px; font-weight: 600; color: var(--idzm-muted); flex-wrap: wrap; }
.idzm-counts b { font-weight: 800; color: var(--idzm-text); font-family: var(--idzm-mono); }

.idzm-honesty { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 16px; }
.idzm-hcard { background: #fff; border: 1px solid var(--idzm-line); border-radius: 13px; padding: 14px 18px; display: flex; align-items: center; gap: 12px; }
.idzm-hicon { width: 34px; height: 34px; border-radius: 9px; background: var(--idzm-amber-soft); color: var(--idzm-amber);
  display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.idzm-hcard .n { font-size: 21px; font-family: var(--idzm-mono); font-weight: 800; color: var(--idzm-amber); }
.idzm-hcard .k { font-size: 11.5px; color: var(--idzm-muted); font-weight: 600; margin-top: 1px; }

.idzm-feed { background: #fff; border: 1px solid var(--idzm-line); border-radius: 16px; padding: 18px 20px; margin-top: 16px; }
.idzm-feed .fh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.idzm-live { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 800; color: var(--idzm-green); }
.idzm-live .b { width: 8px; height: 8px; border-radius: 50%; background: var(--idzm-green); box-shadow: 0 0 0 4px var(--idzm-green-soft); }
.idzm-legend { display: flex; align-items: center; gap: 8px 22px; flex-wrap: wrap; margin: 4px 0 14px;
  padding: 11px 16px; background: #FBFCFE; border: 1px solid var(--idzm-line); border-radius: 11px; }
.idzm-legend .lgk { font-size: 10px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: #9AA6BC; margin-right: 2px; }
.idzm-legend span.item { display: inline-flex; align-items: center; gap: 8px; font-size: 11.5px; font-weight: 700; color: var(--idzm-sub, #3E4C63); white-space: nowrap; }
.idzm-legend i { width: 9px; height: 9px; border-radius: 3px; display: inline-block; flex: 0 0 auto; }
.idzm-dstack { display: flex; flex-direction: column; gap: 9px; }
.idzm-dempty { padding: 26px 8px; color: var(--idzm-muted); font-size: 13px; text-align: center; }
.idzm-dcard { border: 1px solid var(--idzm-line); border-left-width: 4px; border-radius: 12px; padding: 11px 14px;
  display: grid; grid-template-columns: 66px 1fr auto; gap: 12px; align-items: center; cursor: pointer; background: #fff;
  transition: box-shadow .14s ease, border-color .14s ease; }
.idzm-dcard:hover { box-shadow: 0 6px 18px rgba(16,33,74,0.08); }
.idzm-dcard.enter { animation: idzm-slide .42s cubic-bezier(.2,.7,.2,1); }
@keyframes idzm-slide { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
.idzm-dtime { font-family: var(--idzm-mono); font-size: 11.5px; color: var(--idzm-muted); }
.idzm-damt { font-family: var(--idzm-mono); font-size: 12.5px; color: var(--idzm-text); font-weight: 800; margin-top: 2px; }
.idzm-dmid { min-width: 0; }
.idzm-dtrans { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.idzm-dtoday { font-size: 12px; font-weight: 700; color: var(--idzm-muted); text-decoration: line-through; text-decoration-color: #BFC7D6; }
.idzm-darrow { color: #9AA6BC; display: inline-flex; align-items: center; }
.idzm-dnote { font-size: 11px; color: var(--idzm-muted); margin-top: 5px; }
.idzm-dright { text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.idzm-dtag { font-size: 10px; font-weight: 900; letter-spacing: .05em; padding: 3px 9px; border-radius: 6px; white-space: nowrap; }
.idzm-dimpact { font-family: var(--idzm-mono); font-size: 12.5px; font-weight: 800; white-space: nowrap; }
.idzm-dimpact.pos { color: var(--idzm-green); } .idzm-dimpact.neg { color: var(--idzm-red); } .idzm-dimpact.mut { color: var(--idzm-muted); font-weight: 700; }
.idzm-dexp { grid-column: 1 / -1; margin-top: 6px; border-top: 1px solid var(--idzm-line-soft); }
.idzm-t-green { border-left-color: var(--idzm-green); } .idzm-t-green .idzm-dtag { background: var(--idzm-green-soft); color: var(--idzm-green); }
.idzm-t-red { border-left-color: var(--idzm-red); } .idzm-t-red .idzm-dtag { background: var(--idzm-red-soft); color: var(--idzm-red); }
.idzm-t-amber { border-left-color: var(--idzm-amber); } .idzm-t-amber .idzm-dtag { background: var(--idzm-amber-soft); color: var(--idzm-amber); }
.idzm-t-grey { border-left-color: #CBD3E1; } .idzm-t-grey .idzm-dtag { background: var(--idzm-line-soft); color: var(--idzm-muted); } .idzm-t-grey .idzm-dnote, .idzm-t-grey .idzm-dtoday { opacity: .9; }
.idzm-vtoggle { display: inline-flex; background: var(--idzm-line-soft); border: 1px solid var(--idzm-line); border-radius: 9px; padding: 2px; }
.idzm-vtoggle button { border: 0; background: transparent; font-size: 12px; font-weight: 800; color: var(--idzm-muted); padding: 6px 14px; border-radius: 7px; cursor: pointer; }
.idzm-vtoggle button.on { background: #fff; color: var(--idzm-navy); box-shadow: 0 1px 2px rgba(16,33,74,.12); }
.idzm-tblwrap { border: 1px solid var(--idzm-line); border-radius: 12px; overflow-x: auto; }
.idzm-tbl { width: 100%; border-collapse: collapse; table-layout: fixed; min-width: 720px; }
.idzm-tbl thead th { background: #F7F9FD; text-align: left; font-size: 10px; letter-spacing: .06em; text-transform: uppercase;
  color: var(--idzm-muted); font-weight: 800; padding: 9px 12px; border-bottom: 1px solid var(--idzm-line); }
.idzm-tbl th.r, .idzm-tbl td.r { text-align: right; }
.idzm-tbl tbody tr.trow { border-bottom: 1px solid var(--idzm-line-soft); cursor: pointer; }
.idzm-tbl tbody tr.trow:hover { background: #FBFCFF; }
.idzm-tbl td { padding: 10px 12px; font-size: 13px; vertical-align: middle; color: var(--idzm-text); overflow-wrap: normal; word-break: normal; hyphens: none; }
.idzm-tbl tr.flash td { animation: idzm-flash 1s ease; }
@keyframes idzm-flash { from { background: #EAF2FF; } to { background: transparent; } }
.idzm-tbl tr.muted td { color: var(--idzm-muted); } .idzm-tbl tr.muted .idzm-badge { opacity: .82; }
.idzm-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 8px; vertical-align: middle; }
.idzm-arrowc { color: #9AA6BC; text-align: center; }
.idzm-ctag { font-size: 10px; font-weight: 900; letter-spacing: .04em; padding: 3px 9px; border-radius: 6px; white-space: nowrap; display: inline-block; }
.idzm-imp { font-family: var(--idzm-mono); font-size: 12.5px; font-weight: 800; white-space: nowrap; }
.idzm-imp.pos { color: var(--idzm-green); } .idzm-imp.neg { color: var(--idzm-red); } .idzm-imp.mut { color: var(--idzm-muted); font-weight: 600; }
.idzm-tbl td.exp { padding: 2px 12px 14px; background: #FAFBFF; }
.idzm-why { font-size: 12.5px; color: var(--idzm-sub, #3E4C63); line-height: 1.42; }
.idzm-tcap { font-size: 10.5px; color: var(--idzm-muted); margin-top: 5px; line-height: 1.3; }
.idzm-tbl tr.muted .idzm-why { color: var(--idzm-muted); }
.idzm-netbar { margin-top: 14px; border: 1px solid #CFE0F8; border-radius: 14px; background: linear-gradient(100deg, #F3F7FF 0%, #EAF1FE 100%);
  display: flex; align-items: stretch; flex-wrap: wrap; overflow: hidden; }
.idzm-netcell { padding: 14px 20px; display: flex; flex-direction: column; gap: 3px; border-right: 1px solid #D8E4F7; }
.idzm-netcell:last-child { border-right: 0; }
.idzm-netcell.big { background: var(--idzm-navy); color: #fff; margin-left: auto; }
.idzm-netk { font-size: 10.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--idzm-muted); }
.idzm-netcell.big .idzm-netk { color: #AFC7F5; }
.idzm-netv { font-family: var(--idzm-mono); font-size: 20px; font-weight: 800; letter-spacing: -.01em; }
.idzm-netcell.big .idzm-netv { font-size: 26px; color: #5FD3A6; }
.idzm-netsub { font-size: 10.5px; color: var(--idzm-muted); }
.idzm-netcell.big .idzm-netsub { color: #C9D9F7; }
.idzm-table { width: 100%; border-collapse: collapse; }
.idzm-table th { text-align: left; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: var(--idzm-muted); font-weight: 800; padding: 0 8px 10px; }
.idzm-table th.r, .idzm-table td.r { text-align: right; }
.idzm-table tbody tr.row { border-top: 1px solid var(--idzm-line-soft); cursor: pointer; }
.idzm-table tbody tr.row:hover { background: #FBFCFF; }
.idzm-table td { padding: 10px 8px; font-size: 13px; font-weight: 500; vertical-align: middle; color: var(--idzm-text); }
.idzm-newrow { animation: idzm-in .4s ease; }
@keyframes idzm-in { from { background: #EAF2FF; } to { background: transparent; } }
.idzm-scn { font-size: 11px; font-weight: 700; color: var(--idzm-muted); background: var(--idzm-line-soft); border: 1px solid var(--idzm-line); padding: 3px 8px; border-radius: 6px; display: inline-block; line-height: 1.35; }
.idzm-badge { font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 999px; white-space: nowrap; display: inline-block; }
.idzm-b-appr { background: var(--idzm-green-soft); color: var(--idzm-green); }
.idzm-b-decl { background: #EEF1F8; color: var(--idzm-muted); }
.idzm-b-cb { background: var(--idzm-red-soft); color: var(--idzm-red); }
.idzm-b-3ds { background: var(--idzm-amber-soft); color: var(--idzm-amber); }
.idzm-b-deny { background: var(--idzm-red-soft); color: var(--idzm-red); }
.idzm-b-flag { background: var(--idzm-amber-soft); color: var(--idzm-amber); }
.idzm-caret { color: var(--idzm-muted); font-size: 11px; display: inline-block; transition: transform .18s; }
.idzm-table tr.open .idzm-caret { transform: rotate(90deg); }
.idzm-detail td { background: #FAFBFF; padding: 0; }
.idzm-inner { padding: 4px 8px 18px; display: grid; grid-template-columns: 1.1fr 1fr; gap: 22px; }
.idzm-verdict { border-radius: 10px; padding: 11px 14px; margin-bottom: 12px; font-size: 12.5px; font-weight: 600; }
.idzm-v-allow { background: var(--idzm-green-soft); color: #17604A; }
.idzm-v-step { background: var(--idzm-amber-soft); color: #7A4A10; }
.idzm-v-deny { background: var(--idzm-red-soft); color: #98231F; }
.idzm-v-flag { background: var(--idzm-amber-soft); color: #7A4A10; }
.idzm-kv { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dashed var(--idzm-line); font-size: 12.5px; }
.idzm-kv .k { color: var(--idzm-muted); font-weight: 600; }
.idzm-kv .v { font-weight: 700; color: var(--idzm-text); }
.idzm-dh { font-size: 10px; font-weight: 800; letter-spacing: .07em; color: var(--idzm-muted); text-transform: uppercase; margin: 6px 0 9px; }
.idzm-foot { font-size: 11.5px; color: var(--idzm-muted); line-height: 1.7; margin-top: 16px; }
.idzm-foot b { color: var(--idzm-text); }
.idzm-nav { display: flex; align-items: center; gap: 11px; padding: 10px 12px; border-radius: 10px; color: #DCE8FF; font-size: 13.5px; font-weight: 600; }
.idzm-nav-on { background: rgba(255,255,255,0.16); color: #fff; }

.idzm-root button:focus-visible, .idzm-root [tabindex]:focus-visible { outline: 2px solid #1D4ED8; outline-offset: 2px; }
@media (max-width: 1100px) { .idzm-side { display: none; } }
@media (max-width: 920px) { .idzm-lanes, .idzm-honesty, .idzm-inner { grid-template-columns: 1fr; } }
@media (prefers-reduced-motion: reduce) { .idzm-root *, .idzm-prog > div { transition-duration: 0.01ms !important; animation: none !important; } }
`;

function ensureStyles(doc) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  if (!d) return;
  let el = d.getElementById(STYLE_ID);
  if (!el) { el = d.createElement("style"); el.id = STYLE_ID; d.head.appendChild(el); }
  if (el.textContent !== css) el.textContent = css; // keep in sync across hot-reloads / version bumps
}

/* ===================== transaction log (seeded) ====================== */
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function buildDataset(seed = 20260801, N = 1200) {
  const rng = mulberry32(seed);
  const pick = (arr) => arr[Math.floor(rng() * arr.length)];
  const PRODUCTS = ["Metabolic Reset", "Vision Support", "Joint Relief", "Prostate Health", "Blood Sugar Aid", "Sleep Formula", "Collagen Renew"];
  const AMT_TRIAL = [4.95, 6.97, 5.95], AMT_MAIN = [39.9, 49.9, 59.9, 69.95, 79.9, 89.0], AMT_UP = [119.0, 129.0, 149.0, 189.0];
  const BIN_STD = ["414720", "441103", "517805", "552011", "479851", "426938"], BIN_PREPAID = ["485932", "533248"], BIN_HIRISK = ["421761", "404819"];
  const rows = []; let clk = 8 * 3600;
  for (let i = 0; i < N; i++) {
    const r = rng();
    const seg = r < 0.58 ? "clean" : r < 0.89 ? "good" : r < 0.92 ? "fraud" : "rebill";
    clk += 4 + rng() * 90;
    const hh = String(Math.floor((clk / 3600) % 24)).padStart(2, "0"), mm = String(Math.floor((clk / 60) % 60)).padStart(2, "0"), ss = String(Math.floor(clk % 60)).padStart(2, "0");
    let amount, bin, product = pick(PRODUCTS), geo = "US", device = "recognized", note = "", today, idenza, cbToday = false, cbIdz = false, reason = "";
    if (seg === "clean") {
      amount = pick(rng() < 0.25 ? AMT_TRIAL : AMT_MAIN); bin = pick(BIN_STD);
      const declined = rng() < 0.06; today = declined ? "DECLINED" : "APPROVED"; if (declined) reason = "51 Insufficient funds";
      idenza = rng() < 0.018 ? "FP_DENY" : "ALLOW"; cbToday = rng() < 0.003; cbIdz = idenza !== "FP_DENY" && rng() < 0.002;
      note = "device recognized · billing = IP";
    } else if (seg === "good") {
      amount = pick(rng() < 0.5 ? AMT_MAIN : AMT_UP); const hr = rng() < 0.3; bin = hr ? pick(BIN_HIRISK) : pick(BIN_STD);
      geo = rng() < 0.55 ? pick(["UK", "CA", "PH"]) : "US"; device = "new device";
      const declined = rng() < 0.70; today = declined ? "DECLINED" : "APPROVED"; if (declined) reason = pick(["05 Do not honor", "59 Suspected fraud"]);
      const rr = rng(); idenza = rr < 0.66 ? "STEP_OK" : rr < 0.82 ? "STEP_ABANDON" : "STEP_FAIL";
      cbToday = today === "APPROVED" && rng() < 0.01; cbIdz = idenza === "STEP_OK" && rng() < 0.002;
      note = (geo !== "US" ? "geo mismatch · " : "") + (hr ? "high-risk issuer BIN" : "new device");
    } else if (seg === "fraud") {
      const kind = pick(["testing", "velocity", "stolen", "prepaid"]);
      amount = kind === "testing" ? pick(AMT_TRIAL) : pick(kind === "prepaid" ? AMT_UP : AMT_MAIN);
      bin = kind === "prepaid" ? pick(BIN_PREPAID) : pick(BIN_HIRISK.concat(BIN_STD)); device = "unrecognized";
      const appr = rng() < 0.4; today = appr ? "APPROVED_CB" : "DECLINED"; if (!appr) reason = "59 Suspected fraud";
      idenza = rng() < 0.88 ? "DENY" : "ALLOW_SLIP"; cbToday = appr; cbIdz = idenza === "ALLOW_SLIP";
      note = { testing: "card testing · $ probe", velocity: "velocity · cards per IP", stolen: "card on many accounts", prepaid: "prepaid + high amount" }[kind];
    } else {
      amount = pick(AMT_MAIN); bin = pick(BIN_STD); device = "recognized (MIT)";
      const wouldDispute = rng() < 0.12; today = wouldDispute ? "APPROVED_CB" : "APPROVED"; idenza = "FLAG";
      const deflected = rng() < 0.55; cbToday = wouldDispute; cbIdz = wouldDispute && !deflected;
      note = "continuity rebill · evidence armed";
    }
    rows.push({
      i, id: "txn_" + rng().toString(36).slice(2, 6) + "…" + rng().toString(36).slice(2, 5),
      ts: `${hh}:${mm}:${ss}`, seg, product, amount, bin, geo, device, note, reason, today, idenza, cbToday, cbIdz,
      legit: seg !== "fraud",
      apLegitToday: seg !== "fraud" && today !== "DECLINED",
      apLegitIdz: seg !== "fraud" && (idenza === "ALLOW" || idenza === "STEP_OK" || idenza === "FLAG"),
      apAllToday: today === "APPROVED" || today === "APPROVED_CB",
      apAllIdz: idenza === "ALLOW" || idenza === "STEP_OK" || idenza === "FLAG" || idenza === "ALLOW_SLIP",
      abandoned: idenza === "STEP_ABANDON", fp: idenza === "FP_DENY", slip: idenza === "ALLOW_SLIP",
    });
  }
  return rows;
}
function prefixSums(rows) {
  const keys = ["legit", "apLegitToday", "apLegitIdz", "apAllToday", "apAllIdz", "cbToday", "cbIdz", "abandoned", "fp", "slip"];
  const N = rows.length, pre = {};
  keys.forEach((k) => (pre[k] = new Float64Array(N + 1)));
  const money = ["rev", "cba", "cost"];
  money.forEach((k) => (pre[k] = new Float64Array(N + 1)));
  for (let i = 0; i < N; i++) {
    keys.forEach((k) => (pre[k][i + 1] = pre[k][i] + (rows[i][k] ? 1 : 0)));
    const c = classify(rows[i]);
    money.forEach((k) => (pre[k][i + 1] = pre[k][i] + (c[k] || 0)));
  }
  return pre;
}

const pctf = (x) => (x * 100).toFixed(1) + "%";
const bpsf = (x) => Math.round(x * 10000) + " bps";
const numf = (n) => Math.round(n).toLocaleString("en-US");

const TODAY_BADGE = { APPROVED: ["idzm-b-appr", "Approved"], DECLINED: ["idzm-b-decl", "Declined"], APPROVED_CB: ["idzm-b-cb", "Approved → CB"] };
const IDZ_BADGE = {
  ALLOW: ["idzm-b-appr", "Allow"], STEP_OK: ["idzm-b-3ds", "3DS → approved"], STEP_ABANDON: ["idzm-b-decl", "3DS abandoned"],
  STEP_FAIL: ["idzm-b-decl", "3DS failed"], DENY: ["idzm-b-deny", "Deny"], ALLOW_SLIP: ["idzm-b-cb", "Allowed → CB"],
  FLAG: ["idzm-b-flag", "Allow · armed"], FP_DENY: ["idzm-b-deny", "Denied (FP)"],
};
const VERDICT = {
  ALLOW: ["idzm-v-allow", "Clean signals. Approved with no step-up."],
  STEP_OK: ["idzm-v-step", "Good customer, risky signal. Stepped up to 3DS, authenticated, issuer approved, liability shifted."],
  STEP_ABANDON: ["idzm-v-flag", "Stepped up to 3DS but the customer dropped the challenge. A real cost: the sale is lost."],
  STEP_FAIL: ["idzm-v-flag", "Stepped up to 3DS; authentication failed. Held rather than forced through."],
  DENY: ["idzm-v-deny", "Signals stack past the deny line. Denied pre-authorization, so it never settles."],
  ALLOW_SLIP: ["idzm-v-deny", "Scored below the deny line and slipped through. It settles and charges back. Idenza is not perfect."],
  FLAG: ["idzm-v-flag", "Continuity rebill (merchant-initiated, no 3DS). Approved, dispute risk scored, evidence pre-assembled."],
  FP_DENY: ["idzm-v-deny", "Denied but was actually good. A false positive, the cost of a tight threshold."],
};

/* tone -> [tag bg, tag fg, dot] */
const TONE = {
  green: ["#E7F4EC", "#15803D", "#15803D"],
  red: ["#FDECEC", "#DC2626", "#DC2626"],
  amber: ["#FDF2E3", "#C2680B", "#C2680B"],
  grey: ["#EFF3FA", "#64748B", "#CBD3E1"],
};

/* shared expandable reasoning, used by both the table and the row view */
function DecisionDetail({ x }) {
  const v = VERDICT[x.idenza] || ["idzm-v-allow", ""];
  return (
    <div className="idzm-inner">
      <div>
        <div className={"idzm-verdict " + v[0]}>{v[1]}</div>
        <div className="idzm-dh">Transaction</div>
        <div className="idzm-kv"><span className="k">Product</span><span className="v">{x.product}</span></div>
        <div className="idzm-kv"><span className="k">BIN</span><span className="v" style={{ fontFamily: T.mono }}>{x.bin}</span></div>
        <div className="idzm-kv"><span className="k">Billing / IP</span><span className="v">{x.geo}{x.geo !== "US" ? " · mismatch" : ""}</span></div>
        <div className="idzm-kv"><span className="k">Device</span><span className="v">{x.device}</span></div>
      </div>
      <div>
        <div className="idzm-dh">Outcome</div>
        <div className="idzm-kv"><span className="k">Today</span><span className="v">{TODAY_BADGE[x.today][1]}{x.reason ? " · " + x.reason : ""}</span></div>
        <div className="idzm-kv"><span className="k">With Idenza</span><span className="v">{IDZ_BADGE[x.idenza][1]}</span></div>
        <div className="idzm-kv"><span className="k">Signal</span><span className="v">{x.note}</span></div>
        <div className="idzm-kv"><span className="k">Chargeback</span><span className="v">{x.cbToday ? (x.cbIdz ? "still charges back" : "prevented") : "none"}</span></div>
      </div>
    </div>
  );
}

/* what did Idenza change for this transaction, why is it better, and what is it worth.
 * val: numeric contribution to net-vs-today ($). rev = new revenue, cba = chargeback
 * avoided, cost = a sale Idenza lost. A transaction lands in exactly one bucket, so
 * the running totals never double-count. Fraud that slips is a wash vs today (both
 * lanes eat it), so it contributes 0. */
const CB_FEE = 25;
function classify(x) {
  const A = "$" + Math.round(x.amount);
  const cbVal = Math.round(x.amount + CB_FEE);
  if (x.seg === "good" && x.today === "DECLINED" && x.idenza === "STEP_OK")
    return { tone: "green", tag: "RECOVERED", impact: "+" + A, cls: "pos", why: "Real buyer the issuer soft-declined; 3DS proved it", rev: x.amount, cba: 0, cost: 0 };
  if (x.seg === "good" && x.idenza === "STEP_OK")
    return { tone: "green", tag: "PROTECTED", impact: "", cls: "mut", why: "Approved either way, but liability now shifts to the issuer", rev: 0, cba: 0, cost: 0 };
  if (x.seg === "good" && (x.idenza === "STEP_ABANDON" || x.idenza === "STEP_FAIL"))
    return x.today === "APPROVED"
      ? { tone: "amber", tag: "3DS DROPPED", impact: "-" + A, cls: "neg", why: "Customer dropped the 3DS challenge; sale lost", rev: 0, cba: 0, cost: x.amount }
      : { tone: "grey", tag: "NO CHANGE", impact: "", cls: "mut", why: "Declined either way", rev: 0, cba: 0, cost: 0 };
  if (x.seg === "fraud" && x.today === "APPROVED_CB" && x.idenza === "DENY")
    return { tone: "green", tag: "BLOCKED", impact: "+$" + cbVal, cls: "pos", why: "Would settle then charge back; denied before it settles", rev: 0, cba: cbVal, cost: 0 };
  if (x.seg === "fraud" && x.idenza === "ALLOW_SLIP")
    return { tone: "red", tag: "SLIPPED", impact: "-" + A, cls: "mut", why: "Scored below the deny line; not caught (also lost today)", rev: 0, cba: 0, cost: 0 };
  if (x.seg === "fraud")
    return { tone: "grey", tag: "NO CHANGE", impact: "", cls: "mut", why: "Stopped either way", rev: 0, cba: 0, cost: 0 };
  if (x.seg === "rebill" && x.today === "APPROVED_CB" && x.idenza === "FLAG")
    return { tone: "green", tag: "DEFLECTED", impact: "+$" + cbVal, cls: "pos", why: "Dispute-prone rebill; evidence pre-armed to win it", rev: 0, cba: cbVal, cost: 0 };
  if (x.seg === "rebill")
    return { tone: "grey", tag: "ALLOWED", impact: "", cls: "mut", why: "Clean rebill; allowed", rev: 0, cba: 0, cost: 0 };
  if (x.seg === "clean" && x.idenza === "FP_DENY")
    return { tone: "red", tag: "FALSE POSITIVE", impact: "-" + A, cls: "neg", why: "Good customer wrongly denied; a real cost", rev: 0, cba: 0, cost: x.amount };
  return { tone: "grey", tag: "CLEARED", impact: "", cls: "mut", why: "Clean; approved as-is", rev: 0, cba: 0, cost: 0 };
}

/* ============================= sparkline ============================== */
function Spark({ t, i, w = 320, h = 52 }) {
  if (t.length < 2) return <svg width={w} height={h} aria-hidden="true" />;
  const path = (s) => s.map((v, k) => {
    const x = (k / (s.length - 1)) * (w - 4) + 2, y = h - 4 - v * (h - 8);
    return (k ? "L" : "M") + x.toFixed(1) + "," + y.toFixed(1);
  }).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-hidden="true">
      <path d={path(t)} fill="none" stroke="#C3CBE6" strokeWidth="2" />
      <path d={path(i)} fill="none" stroke={T.greenFig} strokeWidth="2.5" />
    </svg>
  );
}

/* ============================ mock sidebar =========================== */
function MockSidebar() {
  const items = ["Rules", "Rulesets", "Sandbox", "Execution Results", "Dashboards"];
  return (
    <aside className="idzm-side">
      <div style={{ background: T.blueBright, borderRadius: 14, padding: "16px 12px", height: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px 14px" }}>
          <div className="idzm-logo" style={{ width: 34, height: 34, fontSize: 18 }}>I</div>
          <span style={{ color: "#fff", fontSize: 17, fontWeight: 800 }}>Navigator</span>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", paddingTop: 12, display: "grid", gap: 4 }}>
          {items.map((l) => <div key={l} className={"idzm-nav" + (l === "Dashboards" ? " idzm-nav-on" : "")}><FiActivity size={15} aria-hidden="true" />{l}</div>)}
        </div>
      </div>
    </aside>
  );
}

/* ================================ page ================================ */
export default function ApprovalUpliftConsole({
  data,
  merchant = "Peak Wellness Co",
  segment = "Nutra · continuity",
  realm = "Idenza",
  updatedAt = "11:06 AM",
  showHeader = true,
  demoShell = false,
  onReplayEnd,
}) {
  useEffect(() => { ensureStyles(); }, []);

  const rows = useMemo(() => (Array.isArray(data) && data.length ? data : buildDataset()), [data]);
  const pre = useMemo(() => prefixSums(rows), [rows]);
  const N = rows.length;

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(6);

  // auto-play once on mount (unless the viewer prefers reduced motion) so the
  // demo already has motion and numbers on screen when a prospect looks
  const autostarted = useRef(false);
  useEffect(() => {
    if (autostarted.current) return;
    autostarted.current = true;
    const reduce = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setIdx(N); return; }
    const id = setTimeout(() => setPlaying(true), 450);
    return () => clearTimeout(id);
  }, [N]);
  const [openId, setOpenId] = useState(null);
  const [feedView, setFeedView] = useState("table");
  const [feedFilter, setFeedFilter] = useState("changes");
  const timer = useRef(null);
  const spark = useRef({ t: [], i: [] });
  const ended = useRef(false);

  useEffect(() => {
    if (!playing) return;
    timer.current = setInterval(() => {
      setIdx((v) => {
        const nv = Math.min(N, v + speed);
        if (nv >= N) setPlaying(false);
        return nv;
      });
    }, 45);
    return () => clearInterval(timer.current);
  }, [playing, speed, N]);

  useMemo(() => {
    const s = spark.current, step = Math.max(1, Math.floor(N / 60));
    s.t = []; s.i = [];
    for (let k = step; k <= idx; k += step) {
      const legit = pre.legit[k] || 1;
      s.t.push(pre.apLegitToday[k] / legit); s.i.push(pre.apLegitIdz[k] / legit);
    }
    if (idx > 0) { const legit = pre.legit[idx] || 1; s.t.push(pre.apLegitToday[idx] / legit); s.i.push(pre.apLegitIdz[idx] / legit); }
  }, [idx, N, pre]);

  const legit = pre.legit[idx] || 1, apAllT = pre.apAllToday[idx] || 1, apAllI = pre.apAllIdz[idx] || 1;
  const today = { appr: pre.apLegitToday[idx] / legit, vamp: pre.cbToday[idx] / apAllT, cb: pre.cbToday[idx] };
  const idz = { appr: pre.apLegitIdz[idx] / legit, vamp: pre.cbIdz[idx] / apAllI, cb: pre.cbIdz[idx], abandoned: pre.abandoned[idx], fp: pre.fp[idx], slip: pre.slip[idx] };
  const idzVampGood = idx > 40 && idz.vamp < 0.015;
  const declinedToday = idx - pre.apAllToday[idx], declinedIdz = idx - pre.apAllIdz[idx];
  const money = { rev: pre.rev[idx], cba: pre.cba[idx], cost: pre.cost[idx] };
  money.net = money.rev + money.cba - money.cost;
  const usd0 = (n) => "$" + Math.round(n).toLocaleString("en-US");

  useEffect(() => {
    if (idx >= N && N > 0 && !ended.current) {
      ended.current = true;
      onReplayEnd && onReplayEnd({ todayAppr: today.appr, idzAppr: idz.appr, todayVamp: today.vamp, idzVamp: idz.vamp });
    }
    if (idx < N) ended.current = false;
  }, [idx, N]); // eslint-disable-line

  const start = useCallback(() => { if (idx >= N) setIdx(0); setPlaying(true); }, [idx, N]);

  const feed = [];
  const feedMax = feedFilter === "changes" ? 9 : 7;
  for (let k = idx - 1; k >= 0 && feed.length < feedMax; k--) {
    if (feedFilter === "changes" && classify(rows[k]).tone === "grey") continue;
    feed.push(rows[k]);
  }
  const badge = (spec) => <span className={"idzm-badge " + spec[0]}>{spec[1]}</span>;

  const vars = {
    "--idzm-navy": T.navy, "--idzm-blue": T.blue, "--idzm-green": T.green, "--idzm-green-soft": T.greenSoft,
    "--idzm-amber": T.amber, "--idzm-amber-soft": T.amberSoft, "--idzm-red": T.red, "--idzm-red-soft": T.redSoft,
    "--idzm-line": T.line, "--idzm-line-soft": T.lineSoft, "--idzm-text": T.text, "--idzm-muted": T.muted, "--idzm-mono": T.mono,
  };

  const page = (
    <div className="idzm-page">
      <div className="idzm-wrap">
        {showHeader && (
          <header className="idzm-banner">
            <div className="idzm-logo">I</div>
            <div style={{ minWidth: 0 }}>
              <div className="idzm-eyebrow" style={{ color: "#AFC7F5" }}>APPROVAL &amp; DEFLECTION</div>
              <h1 style={{ fontSize: 23, fontWeight: 800, letterSpacing: "-0.02em", margin: "2px 0 0", color: "#fff" }}>
                What Idenza changes for a merchant
              </h1>
              <p style={{ color: "#C9D9F7", fontSize: 13, margin: "4px 0 0" }}>
                One month of {merchant}’s traffic, replayed with and without Idenza scoring pre-authorization.
              </p>
            </div>
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <span style={{ background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 999, display: "inline-block" }}>
                Realm · {realm}
              </span>
              <div style={{ color: "#AFC7F5", fontSize: 11.5, marginTop: 8, fontFamily: T.mono }}>Representative traffic · not live</div>
            </div>
          </header>
        )}

        <div style={{ marginTop: showHeader ? 20 : 2 }}>
          <div className="idzm-eyebrow" style={{ color: T.blue }}>SAME TRANSACTIONS, TWO WORLDS</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.025em", margin: "3px 0 0", color: T.text }}>
            {merchant} <span style={{ color: T.muted, fontWeight: 700, fontSize: 15 }}> · {segment}</span>
          </h2>
          <p style={{ fontSize: 13, color: T.sub, margin: "4px 0 0" }}>
            Press play. Every transaction is scored in both lanes at once; the rates build out of the individual decisions.
          </p>
        </div>

        <div className="idzm-ctrl">
          <button className="idzm-play" onClick={() => (playing ? setPlaying(false) : start())}>
            {playing ? <><FiPause size={15} /> Pause</> : idx >= N ? <><FiPlay size={15} /> Replay</> : idx > 0 ? <><FiPlay size={15} /> Resume</> : <><FiPlay size={15} /> Play the month</>}
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            {[["1×", 2], ["4×", 6], ["12×", 18]].map(([l, v]) => (
              <button key={l} className={"idzm-sbtn" + (speed === v ? " on" : "")} onClick={() => setSpeed(v)}>{l}</button>
            ))}
            <button className="idzm-sbtn" onClick={() => { setIdx(N); setPlaying(false); }}><FiSkipForward size={13} /> Skip to end</button>
          </div>
          <div className="idzm-prog"><div style={{ width: (idx / N) * 100 + "%" }} /></div>
          <div className="idzm-progtxt">{numf(idx)} / {numf(N)} txns</div>
        </div>

        <div className="idzm-lanes">
          <div className="idzm-lane">
            <div className="lh"><div className="lt" style={{ color: T.muted }}>MERCHANT TODAY</div>
              <span className="idzm-tag" style={{ background: T.lineSoft, color: T.muted }}>issuer decides</span></div>
            <div className="idzm-duo">
              <div className="idzm-big"><div className="k">Approval · legit</div><div className="v" style={{ color: T.text }}>{idx ? pctf(today.appr) : "—"}</div></div>
              <div className="idzm-big"><div className="k">VAMP</div><div className="v" style={{ color: today.vamp >= 0.015 ? T.red : T.text }}>{idx ? bpsf(today.vamp) : "—"}</div></div>
            </div>
            <div className="idzm-counts"><span>Approved <b>{numf(pre.apAllToday[idx])}</b></span><span>Declined <b>{numf(declinedToday)}</b></span><span>Chargebacks <b>{numf(today.cb)}</b></span></div>
          </div>
          <div className="idzm-lane idz">
            <div className="lh"><div className="lt" style={{ color: T.blue }}>WITH IDENZA</div>
              <span className="idzm-tag" style={{ background: T.blueSoft, color: T.blue }}>scored pre-auth</span></div>
            <div className="idzm-duo">
              <div className="idzm-big"><div className="k">Approval · legit</div><div className="v" style={{ color: idx ? T.greenFig : T.text }}>{idx ? pctf(idz.appr) : "—"}</div></div>
              <div className="idzm-big"><div className="k">VAMP</div><div className="v" style={{ color: idx ? (idzVampGood ? T.greenFig : T.red) : T.text }}>{idx ? bpsf(idz.vamp) : "—"}</div></div>
            </div>
            <div className="idzm-counts"><span>Approved <b>{numf(pre.apAllIdz[idx])}</b></span><span>Declined <b>{numf(declinedIdz)}</b></span><span>Chargebacks <b>{numf(idz.cb)}</b></span></div>
            <div style={{ marginTop: 12 }}><Spark t={spark.current.t} i={spark.current.i} /></div>
          </div>
        </div>

        <div className="idzm-honesty">
          {[[FiZap, idz.abandoned, "3DS challenges abandoned · lost sales"],
            [FiShield, idz.fp, "False positives · good customers denied"],
            [FiAlertTriangle, idz.slip, "Fraud that still slipped through"]].map(([Icon, n, k], j) => (
            <div key={j} className="idzm-hcard">
              <div className="idzm-hicon"><Icon size={17} aria-hidden="true" /></div>
              <div><div className="n">{numf(n)}</div><div className="k">{k}</div></div>
            </div>
          ))}
        </div>

        <div className="idzm-feed">
          <div className="fh">
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Live decisions</div>
              <div style={{ fontSize: 12, color: T.muted }}>Where Idenza changes the outcome versus running today. Select a row for the reasoning.</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {playing && <span className="idzm-live"><span className="b" />streaming</span>}
              <div className="idzm-vtoggle" role="tablist" aria-label="Filter">
                <button role="tab" aria-selected={feedFilter === "changes"} className={feedFilter === "changes" ? "on" : ""} onClick={() => setFeedFilter("changes")}>Changes</button>
                <button role="tab" aria-selected={feedFilter === "all"} className={feedFilter === "all" ? "on" : ""} onClick={() => setFeedFilter("all")}>All</button>
              </div>
              <div className="idzm-vtoggle" role="tablist" aria-label="View">
                <button role="tab" aria-selected={feedView === "table"} className={feedView === "table" ? "on" : ""} onClick={() => setFeedView("table")}>Table</button>
                <button role="tab" aria-selected={feedView === "rows"} className={feedView === "rows" ? "on" : ""} onClick={() => setFeedView("rows")}>Rows</button>
              </div>
            </div>
          </div>
          <div className="idzm-legend">
            <span className="lgk">Legend</span>
            <span className="item"><i style={{ background: T.green }} />Recovered / blocked / deflected</span>
            <span className="item"><i style={{ background: T.amber }} />Cost of a step-up</span>
            <span className="item"><i style={{ background: T.red }} />Miss or false positive</span>
            <span className="item"><i style={{ background: "#CBD3E1" }} />No change</span>
          </div>

          {idx > 0 && (
            <div className="idzm-netbar" aria-live="polite">
              <div className="idzm-netcell">
                <span className="idzm-netk">Revenue recovered</span>
                <span className="idzm-netv" style={{ color: T.greenFig }}>{usd0(money.rev)}</span>
                <span className="idzm-netsub">good sales the issuer was declining</span>
              </div>
              <div className="idzm-netcell">
                <span className="idzm-netk">Chargebacks avoided</span>
                <span className="idzm-netv" style={{ color: T.greenFig }}>{usd0(money.cba)}</span>
                <span className="idzm-netsub">denied pre-auth or deflected with evidence</span>
              </div>
              <div className="idzm-netcell">
                <span className="idzm-netk">Cost of step-ups &amp; FPs</span>
                <span className="idzm-netv" style={{ color: T.red }}>-{usd0(money.cost)}</span>
                <span className="idzm-netsub">abandoned 3DS and false positives</span>
              </div>
              <div className="idzm-netcell big">
                <span className="idzm-netk">Net impact this month</span>
                <span className="idzm-netv">+{usd0(money.net)}</span>
                <span className="idzm-netsub">vs how the merchant runs today</span>
              </div>
            </div>
          )}

          {feedView === "table" ? (
            <div className="idzm-tblwrap">
              <table className="idzm-tbl">
                <thead><tr>
                  <th style={{ width: 78 }}>Time</th>
                  <th className="r" style={{ width: 78 }}>Amount</th>
                  <th style={{ width: 196 }}>Today → Idenza</th>
                  <th>Why it improves</th>
                  <th className="r" style={{ width: 92 }}>Impact</th>
                  <th style={{ width: 22 }} aria-hidden="true" />
                </tr></thead>
                <tbody>
                  {feed.length === 0 && <tr><td colSpan={6} className="idzm-dempty">{idx === 0 ? "Press play to start scoring the month." : "No changed decisions in view yet \u2014 they stream in as the month runs."}</td></tr>}
                  {feed.map((x, pos) => {
                    const isOpen = openId === x.i, c = classify(x), tn = TONE[c.tone];
                    return (
                      <React.Fragment key={x.i}>
                        <tr className={"trow" + (pos === 0 ? " flash" : "") + (c.tone === "grey" ? " muted" : "")}
                            role="button" tabIndex={0} aria-expanded={isOpen}
                            onClick={() => setOpenId(isOpen ? null : x.i)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(isOpen ? null : x.i); } }}>
                          <td><span className="idzm-dot" style={{ background: tn[2] }} /><span style={{ fontFamily: T.mono, fontSize: 12, color: T.muted }}>{x.ts}</span></td>
                          <td className="r" style={{ fontFamily: T.mono, fontWeight: 700 }}>${x.amount.toFixed(2)}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span className="idzm-dtoday">{TODAY_BADGE[x.today][1]}</span>
                              <FiArrowRight size={12} aria-hidden="true" style={{ color: "#9AA6BC" }} />
                              <span className={"idzm-badge " + IDZ_BADGE[x.idenza][0]}>{IDZ_BADGE[x.idenza][1]}</span>
                            </div>
                            <div className="idzm-tcap">{x.note}</div>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                              <span className="idzm-ctag" style={{ background: tn[0], color: tn[1], flex: "0 0 auto", marginTop: 1 }}>{c.tag}</span>
                              <span className="idzm-why">{c.why}</span>
                            </div>
                          </td>
                          <td className="r">{c.impact ? <span className={"idzm-imp " + c.cls}>{c.impact}</span> : <span style={{ color: T.muted }}>—</span>}</td>
                          <td className="r"><FiChevronRight size={14} aria-hidden="true" style={{ color: T.muted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .18s" }} /></td>
                        </tr>
                        {isOpen && <tr><td className="exp" colSpan={6}><DecisionDetail x={x} /></td></tr>}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="idzm-dstack">
              {feed.length === 0 && <div className="idzm-dempty">{idx === 0 ? "Press play to start scoring the month." : "No changed decisions in view yet \u2014 they stream in as the month runs."}</div>}
              {feed.map((x, pos) => {
                const isOpen = openId === x.i, c = classify(x);
                return (
                  <div key={x.i} className={"idzm-dcard idzm-t-" + c.tone + (pos === 0 ? " enter" : "")}
                       role="button" tabIndex={0} aria-expanded={isOpen}
                       onClick={() => setOpenId(isOpen ? null : x.i)}
                       onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(isOpen ? null : x.i); } }}>
                    <div>
                      <div className="idzm-dtime">{x.ts}</div>
                      <div className="idzm-damt">${x.amount.toFixed(2)}</div>
                    </div>
                    <div className="idzm-dmid">
                      <div className="idzm-dtrans">
                        <span className="idzm-dtoday">{TODAY_BADGE[x.today][1]}</span>
                        <FiArrowRight className="idzm-darrow" size={13} aria-hidden="true" />
                        <span className={"idzm-badge " + IDZ_BADGE[x.idenza][0]}>{IDZ_BADGE[x.idenza][1]}</span>
                      </div>
                      <div className="idzm-dnote">{x.note}</div>
                    </div>
                    <div className="idzm-dright">
                      <span className="idzm-dtag">{c.tag}</span>
                      {c.impact && <span className={"idzm-dimpact " + c.cls}>{c.impact}</span>}
                    </div>
                    {isOpen && <div className="idzm-dexp"><DecisionDetail x={x} /></div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="idzm-foot">
          <b>Why this is honest.</b> Both lanes score the exact same transactions, so nothing is cherry-picked. Idenza’s costs are on screen: abandoned 3DS challenges are lost sales, false positives are good customers turned away, and some fraud still slips. The rates are computed from the decisions above, not asserted.<br />
          <b>3DS vs rebills.</b> The approval lift is 3DS on the initial purchase. Rebills are merchant-initiated with no 3DS; Idenza cuts those disputes with risk scoring and evidence, shown as “allow · armed,” not authentication.<br />
          <b>Provenance.</b> Representative synthetic month with realistic distributions, not live results. Pass a real anonymized export as <code>data</code> and every number becomes real with no other change.
        </p>
      </div>
    </div>
  );

  return (
    <div className="idzm-root" style={{ ...vars, background: T.page, color: T.text, fontFamily: T.sans, minHeight: "100%" }}>
      {demoShell ? <div className="idzm-shell"><MockSidebar />{page}</div> : page}
    </div>
  );
}
